import { useMemo } from 'react';
import { useIsMutating, useQuery } from '@tanstack/react-query';
import { coverageOptionsBySchedule, quotesWithOffered, type ConsumerQuote } from '@shared/consumerPlan';
import { consumerApi, consumerCarePlanSaveKey, consumerQuoteKeys } from '../api/consumer-api';

export function useConsumerQuotes(clientId: string) {
  const query = useQuery({
    queryKey: consumerQuoteKeys.detail(clientId),
    queryFn: () => consumerApi.getQuotes(clientId),
    enabled: !!clientId,
    staleTime: 5 * 60 * 1000,
  });
  // A care plan save keeps going until the quotes are re-priced on it (see useCarePlanEditor).
  const isRepricing = useIsMutating({ mutationKey: consumerCarePlanSaveKey(clientId) }) > 0;

  // `quotes` holds every quote a saved key can point at, including the re-priced options.
  const { quotes, coverageOptions } = useMemo(() => {
    const stored = (query.data?.quotes ?? []) as ConsumerQuote[];
    const options = query.data?.coverageOptions ?? coverageOptionsBySchedule(stored);
    return { quotes: quotesWithOffered(stored, options, query.data?.chosenQuote), coverageOptions: options };
  }, [query.data]);

  return {
    quotes,
    coverageOptions,
    carrierCount: query.data?.carrierCount ?? 0,
    usaState: query.data?.usaState ?? null,
    noQuotesReason: query.data?.noQuotesReason,
    isLoading: query.isLoading || isRepricing,
    isError: query.isError,
    refetch: query.refetch,
  };
}
